import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/api/client";
import { Trash2, Plus, X } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";

/**
 * Generic CMS list + edit component.
 *
 * Props:
 *  resource: e.g. "properties"
 *  title: display name (e.g. "Imóveis")
 *  singular: e.g. "Imóvel"
 *  fields: array of { name, label, type, options?, placeholder?, help? }
 *    types: text, textarea, number, checkbox, select, list, image, image-list, list-object
 *  defaults: default object for new item
 *  displayColumns: array of field names to show in the table
 */
export default function CmsResource({ resource, title, singular, fields, defaults, displayColumns }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // object or null
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/${resource}`);
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [resource]);

  const startCreate = () => setEditing({ ...defaults });
  const startEdit = (it) => setEditing({ ...defaults, ...it });
  const closeEdit = () => setEditing(null);

  const onChange = (name, value) => setEditing((e) => ({ ...e, [name]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...editing };
      // Remove server-managed fields before send
      delete payload.id;
      delete payload._id;
      delete payload.created_at;
      delete payload.updated_at;

      if (editing.id) {
        const { data } = await api.put(`/admin/${resource}/${editing.id}`, payload);
        if (payload.slug && data?.slug && data.slug !== payload.slug) {
          toast.success(`${singular} atualizado. Slug ajustado para “${data.slug}” (já existia).`);
        } else {
          toast.success(`${singular} atualizado.`);
        }
      } else {
        const { data } = await api.post(`/admin/${resource}`, payload);
        if (payload.slug && data?.slug && data.slug !== payload.slug) {
          toast.success(`${singular} criado. Slug ajustado para “${data.slug}” (já existia).`);
        } else {
          toast.success(`${singular} criado.`);
        }
      }
      closeEdit();
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Confirma que quer eliminar? Esta ação é definitiva.")) return;
    try {
      await api.delete(`/admin/${resource}/${id}`);
      toast.success(`${singular} eliminado.`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const renderField = (f) => {
    const v = editing[f.name];
    const commonCls = "w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm";
    if (f.type === "textarea") {
      return <textarea rows={f.rows || 4} value={v || ""} onChange={(e) => onChange(f.name, e.target.value)} className={commonCls} data-testid={`cms-field-${f.name}`} />;
    }
    if (f.type === "number") {
      return <input type="number" value={v ?? ""} onChange={(e) => onChange(f.name, e.target.value === "" ? "" : Number(e.target.value))} className={commonCls} data-testid={`cms-field-${f.name}`} />;
    }
    if (f.type === "checkbox") {
      return <label className="flex items-center gap-2 text-sm text-stone-700"><input type="checkbox" checked={!!v} onChange={(e) => onChange(f.name, e.target.checked)} data-testid={`cms-field-${f.name}`} /> {f.checkboxLabel || f.label}</label>;
    }
    if (f.type === "select") {
      return (
        <select value={v || ""} onChange={(e) => onChange(f.name, e.target.value)} className={commonCls} data-testid={`cms-field-${f.name}`}>
          <option value="">—</option>
          {f.options.map((o) => (<option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>))}
        </select>
      );
    }
    if (f.type === "list") {
      const arr = Array.isArray(v) ? v : [];
      return (
        <div className="space-y-2" data-testid={`cms-field-${f.name}`}>
          {arr.map((s, i) => (
            <div key={i} className="flex gap-2">
              <input value={s} onChange={(e) => { const n = [...arr]; n[i] = e.target.value; onChange(f.name, n); }} className={commonCls} />
              <button type="button" onClick={() => { const n = arr.filter((_, j) => j !== i); onChange(f.name, n); }} className="rounded-lg border border-stone-200 px-2 text-stone-500"><X className="h-4 w-4" /></button>
            </div>
          ))}
          <button type="button" onClick={() => onChange(f.name, [...arr, ""])} className="btn-ghost text-sm"><Plus className="h-4 w-4" /> Adicionar</button>
        </div>
      );
    }
    if (f.type === "image") {
      return <ImageUploader value={v || ""} onChange={(val) => onChange(f.name, val)} multiple={false} testid={`cms-image-${f.name}`} />;
    }
    if (f.type === "image-list") {
      return <ImageUploader value={Array.isArray(v) ? v : []} onChange={(val) => onChange(f.name, val)} multiple testid={`cms-images-${f.name}`} />;
    }
    if (f.type === "list-object") {
      const arr = Array.isArray(v) ? v : [];
      return (
        <div className="space-y-3" data-testid={`cms-field-${f.name}`}>
          {arr.map((obj, i) => (
            <div key={i} className="rounded-lg border border-stone-200 p-3 space-y-2 bg-stone-50">
              {f.subfields.map((sf) => (
                <div key={sf.name}>
                  <div className="text-xs text-stone-500 mb-1">{sf.label}</div>
                  <input value={obj[sf.name] || ""} onChange={(e) => { const n = [...arr]; n[i] = { ...obj, [sf.name]: e.target.value }; onChange(f.name, n); }} className={commonCls} />
                </div>
              ))}
              <button type="button" onClick={() => onChange(f.name, arr.filter((_, j) => j !== i))} className="text-xs text-red-700">Remover</button>
            </div>
          ))}
          <button type="button" onClick={() => onChange(f.name, [...arr, Object.fromEntries(f.subfields.map((sf) => [sf.name, ""]))])} className="btn-ghost text-sm"><Plus className="h-4 w-4" /> Adicionar</button>
        </div>
      );
    }
    // default: text / image (single url)
    return <input value={v ?? ""} onChange={(e) => onChange(f.name, e.target.value)} className={commonCls} placeholder={f.placeholder || ""} data-testid={`cms-field-${f.name}`} />;
  };

  return (
    <div data-testid={`cms-${resource}`}>
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl text-stone-900">{title}</h1>
        <button onClick={startCreate} data-testid={`cms-${resource}-new`} className="btn-primary"><Plus className="h-4 w-4" /> Novo {singular}</button>
      </div>

      <div className="mt-8 card-soft overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 border-b border-stone-200">
            <tr>
              {displayColumns.map((c) => (<th key={c.key} className="text-left px-4 py-3 text-xs uppercase tracking-widest text-stone-500">{c.label}</th>))}
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={displayColumns.length + 1} className="p-6 text-stone-500">A carregar...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={displayColumns.length + 1} className="p-6 text-stone-500">Nenhum registo ainda.</td></tr>
            ) : items.map((it) => (
              <tr key={it.id} className="border-b border-stone-100 hover:bg-stone-50/60">
                {displayColumns.map((c) => (
                  <td key={c.key} className="px-4 py-3 text-stone-800">{c.render ? c.render(it) : String(it[c.key] ?? "—")}</td>
                ))}
                <td className="px-4 py-3 text-right">
                  <button onClick={() => startEdit(it)} data-testid={`cms-${resource}-edit-${it.id}`} className="btn-ghost">Editar</button>
                  <button onClick={() => remove(it.id)} data-testid={`cms-${resource}-delete-${it.id}`} className="btn-ghost text-red-700"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-stone-900/40 backdrop-blur-sm flex items-start justify-center p-6 overflow-y-auto" onClick={closeEdit}>
          <div className="bg-white rounded-2xl w-full max-w-3xl p-8 my-10 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={closeEdit} className="absolute right-4 top-4 rounded-full p-2 text-stone-500 hover:bg-stone-100"><X className="h-5 w-5" /></button>
            <h2 className="font-display text-2xl text-stone-900">{editing.id ? `Editar ${singular}` : `Novo ${singular}`}</h2>
            <div className="mt-6 grid gap-5 grid-cols-1 md:grid-cols-2">
              {fields.map((f) => (
                <div key={f.name} className={f.wide ? "md:col-span-2" : ""}>
                  <label className="block text-xs uppercase tracking-widest text-stone-500 mb-2">{f.label}</label>
                  {renderField(f)}
                  {f.help && <div className="mt-1 text-xs text-stone-500">{f.help}</div>}
                </div>
              ))}
            </div>
            <div className="mt-8 flex justify-end gap-3">
              <button onClick={closeEdit} className="btn-secondary" data-testid={`cms-${resource}-cancel`}>Cancelar</button>
              <button onClick={save} disabled={saving} className="btn-primary" data-testid={`cms-${resource}-save`}>{saving ? "A guardar..." : "Guardar"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
